import { useState, useRef, useEffect } from "react";
import "./style.css";
import StoryReply from "./StoryReply";

const API_ROOT = process.env.REACT_APP_API_URL || '';

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  if (API_ROOT) {
    return `${API_ROOT.replace(/\/$/, '')}${normalizedPath}`;
  }
  return normalizedPath;
};

const isPdfFile = (media = {}) => {
  const name = (media.name || '').toLowerCase();
  const url = (media.url || media.path || '').toLowerCase();
  const mime = (media.mimetype || '').toLowerCase();
  return mime === 'application/pdf' || name.endsWith('.pdf') || url.endsWith('.pdf');
};

// Build the user-facing text for a `type === 'system'` message. We prefer the
// pre-rendered text from the server, but fall back to a sensible default per
// known action. When the current viewer is the subject of the action we
// personalize the copy ("You were removed…") so it reads naturally.
const formatSystemMessageText = (message, currentUserId) => {
  const data = message?.content?.system?.data || {};
  const action = message?.content?.system?.action;
  const subjectId = data?.userId ? String(data.userId) : '';
  const viewerId = currentUserId ? String(currentUserId) : '';
  const isViewerSubject = !!subjectId && !!viewerId && subjectId === viewerId;

  if (action === 'user_removed' && isViewerSubject) {
    return 'You were removed from this group by an admin.';
  }
  if (action === 'user_left' && isViewerSubject) {
    return 'You left the group';
  }

  if (message?.content?.text) return message.content.text;
  switch (action) {
    case 'user_left':
      return `${data.userName || 'A user'} left the group`;
    case 'user_added': {
      const actor = data.actorName || 'Admin';
      const member = data.userName || 'a new member';
      return `${actor} added ${member}`;
    }
    case 'user_removed': {
      const actor = data.actorName || 'Admin';
      const member = data.userName || 'a member';
      return `${actor} removed ${member}`;
    }
    default:
      return 'Group updated';
  }
};

// Helper function to format date for day separators
const formatDateForSeparator = (date) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Reset time to compare only dates
  const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  
  if (messageDateOnly.getTime() === todayOnly.getTime()) {
    return "Today";
  } else if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
    return "Yesterday";
  } else {
    // For other dates, show the full date
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
};

// Helper function to check if two dates are on the same day
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

// Helper: true only when message is exactly one emoji (big WhatsApp-style); 2+ emojis stay small
const isSingleEmoji = (text) => {
  if (!text || typeof text !== 'string') return false;
  const t = text.trim();
  if (!t) return false;
  try {
    if (!/^[\p{Emoji}\s]+$/u.test(t)) return false;
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    const segments = [...segmenter.segment(t)].filter((s) => s.segment.trim() !== '');
    if (segments.length !== 1) return false;
    return /\p{Emoji}/u.test(segments[0].segment);
  } catch {
    return false;
  }
};

const MessageBubble = ({ message, currentUser, onReact, onDelete, onReply, onStar, onEdit, onForward, conversation, isSelecting, isSelected, onToggleSelect, starRefreshKey, onShowMessageInfo, disableInteractions, showSenderName: propShowSenderName, showAvatar: propShowAvatar, showMeta = true }) => {
  const MESSAGE_EDIT_WINDOW_MINUTES = 10;
  // WhatsApp-style "Delete for everyone" grace period. Past this window the
  // option is hidden and the sender can only delete the message for themself.
  const MESSAGE_DELETE_FOR_EVERYONE_WINDOW_MINUTES = 2 * 24 * 60; // 48 hours
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message.content?.text || '');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Local star state persisted in localStorage as fallback if parent doesn't persist
  const [isStarredLocal, setIsStarredLocal] = useState(() => {
    try {
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      return !!(message.starred || message.isStarred || store[message._id]);
    } catch {
      return !!(message.starred || message.isStarred);
    }
  });
  // Get downloaded files from localStorage or initialize empty object
  const [downloadedFiles, setDownloadedFiles] = useState(() => {
    const saved = localStorage.getItem('downloadedFiles');
    return saved ? JSON.parse(saved) : {};
  });
  const audioRef = useRef(null);
  const menuRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  
  const handleFileDownload = async (media, index) => {
    const fileKey = `${message._id}-${index}`;
    
    try {
      let blob, fileName;
      
      // If we have a direct URL, use it
      if (media.url) {
        const fileUrl = resolveMediaUrl(media.url);
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error('Failed to download file');
        blob = await response.blob();
        fileName = media.name || 'download';
      } else if (media.file) {
        // If we have a file object directly
        blob = media.file;
        fileName = media.name || media.file.name || 'download';
      } else {
        console.error('No file data available for download');
        alert('File download is not available');
        return;
      }
      
      // Create a blob URL for the file
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      
      // For PDF files, add a special attribute to force download behavior
      if (fileName.toLowerCase().endsWith('.pdf')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      // Mark this file as downloaded in state and localStorage
      const newDownloadedFiles = {
        ...downloadedFiles,
        [fileKey]: true
      };
      
      setDownloadedFiles(newDownloadedFiles);
      localStorage.setItem('downloadedFiles', JSON.stringify(newDownloadedFiles));
      
    } catch (error) {
      console.error('Error downloading file:', error);
      if (media?.url) {
        window.open(resolveMediaUrl(media.url), '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to download file. Please try again.');
      }
    }
  };
  
  const isOwnMessage = message.sender._id === currentUser.id;
  const isDeleted = message.deleted?.isDeleted;
  const isGroupChat = conversation?.type === 'group';
  const isWithinEditWindow = (() => {
    const createdAtMs = new Date(message.createdAt).getTime();
    if (!createdAtMs) return false;
    return Date.now() - createdAtMs <= MESSAGE_EDIT_WINDOW_MINUTES * 60 * 1000;
  })();
  const isWithinDeleteForEveryoneWindow = (() => {
    const createdAtMs = new Date(message.createdAt).getTime();
    if (!createdAtMs) return false;
    return Date.now() - createdAtMs <= MESSAGE_DELETE_FOR_EVERYONE_WINDOW_MINUTES * 60 * 1000;
  })();
  // allow parent to override showing avatar/name for compact consecutive messages
  const showSenderName = typeof propShowSenderName !== 'undefined' ? propShowSenderName : (isGroupChat && !isOwnMessage);
  const showAvatar = typeof propShowAvatar !== 'undefined' ? propShowAvatar : (!isOwnMessage);

  const formatTime = (date) => {
    const messageDate = new Date(date);
    
    // Show only time in 12-hour format with lowercase am/pm
    const timeString = messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    // Convert AM/PM to lowercase am/pm and remove the space
    return timeString.replace(' AM', 'am').replace(' PM', 'pm');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const closeMenu = () => {
    setShowMenu(false);
  };

  const startEdit = () => {
    if (!isWithinEditWindow) return;
    setEditedText(message.content?.text || '');
    setIsEditing(true);
    closeMenu();
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedText(message.content?.text || '');
  };

  const submitEdit = async () => {
    const nextText = (editedText || '').trim();
    const currentText = (message.content?.text || '').trim();
    if (!nextText || nextText === currentText) {
      cancelEdit();
      return;
    }
    if (onEdit) {
      await onEdit(message._id, nextText);
    }
    setIsEditing(false);
  };

  const handleReaction = (emoji) => {
    closeMenu();
    onReact(message._id, emoji);
  };

  const forwardableTypes = ['text', 'image', 'video', 'audio', 'file', 'contact'];
  const canForward = !message.deleted?.isDeleted && forwardableTypes.includes(message.type);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchStart = (event) => {
    if (isSelecting || disableInteractions) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      setShowMenu(true);
    }, 500);
  };

  const handleTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    if (dx > 8 || dy > 8) {
      clearLongPressTimer();
    }
  };

  const handleStar = () => {
    closeMenu();
    if (onStar) {
      onStar(message);
    }
    setIsStarredLocal(prev => {
      const next = !prev;
      try {
        const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
        if (next) {
          store[message._id] = true;
        } else {
          delete store[message._id];
        }
        localStorage.setItem('starredMessages', JSON.stringify(store));

        const dataStore = JSON.parse(localStorage.getItem('starredMessagesData') || '{}');
        if (next) {
          const preview =
            message.type === 'text' ? (message.content?.text || '') :
            message.type === 'image' ? '[Image]' :
            message.type === 'video' ? '[Video]' :
            message.type === 'audio' ? '[Audio]' :
            message.type === 'file' ? '[File]' :
            message.type === 'contact' ? `[Contact] ${message.content?.contact?.name || ''}` :
            message.type === 'location' ? '[Location]' :
            message.type === 'call' ? '[Call]' : '[Message]';

          dataStore[message._id] = {
            messageId: message._id,
            conversationId: conversation?._id,
            senderId: message.sender?._id || '',
            senderName: `${message.sender?.first_name || ''} ${message.sender?.last_name || ''}`.trim() || 'Unknown User',
            senderPicture: message.sender?.picture || '',
            preview,
            createdAt: message.createdAt,
            starredAt: new Date().toISOString(),
          };
        } else {
          delete dataStore[message._id];
        }
        localStorage.setItem('starredMessagesData', JSON.stringify(dataStore));
        window.dispatchEvent(new CustomEvent('starredMessagesUpdated'));
      } catch {}
      return next;
    });
  };

  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioError = () => {
    console.error('Error loading audio file');
    setIsPlaying(false);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleAudioPlayEvent = () => {
    setIsPlaying(true);
  };

  const handleAudioPauseEvent = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('timeupdate', handleAudioTimeUpdate);
      audio.addEventListener('loadedmetadata', handleAudioLoadedMetadata);
      audio.addEventListener('ended', handleAudioEnded);
      audio.addEventListener('play', handleAudioPlayEvent);
      audio.addEventListener('pause', handleAudioPauseEvent);
      audio.addEventListener('error', handleAudioError);

      return () => {
        audio.removeEventListener('timeupdate', handleAudioTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleAudioLoadedMetadata);
        audio.removeEventListener('ended', handleAudioEnded);
        audio.removeEventListener('play', handleAudioPlayEvent);
        audio.removeEventListener('pause', handleAudioPauseEvent);
        audio.removeEventListener('error', handleAudioError);
      };
    }
  }, []);

  const quickReactions = ['👍', '❤️', '😆', '😮', '😢', '😡'];

  // Use local starred state; sync from props below when provided
  const isStarred = isStarredLocal;

  useEffect(() => {
    // Sync local state if parent supplies updated message with starred flag
    if ((message.starred || message.isStarred) !== undefined) {
      const newVal = !!(message.starred || message.isStarred);
      setIsStarredLocal(newVal);
      try {
        const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
        if (newVal) {
          store[message._id] = true;
        } else {
          delete store[message._id];
        }
        localStorage.setItem('starredMessages', JSON.stringify(store));
      } catch {}
    }
  }, [message.starred, message.isStarred, message._id]);

  // Refresh starred state when bulk changes happen
  useEffect(() => {
    try {
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      setIsStarredLocal(!!store[message._id] || !!(message.starred || message.isStarred));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starRefreshKey]);

  useEffect(() => {
    if (!showMenu) return undefined;

    const handlePointerDownOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    return () => clearLongPressTimer();
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setEditedText(message.content?.text || '');
    }
  }, [message.content?.text, isEditing]);

  if (isDeleted) {
    return (
      <div className={`message_bubble ${isOwnMessage ? 'own' : 'other'} deleted`}>
        <div className="message_content">
          <span className="deleted_message">This message was deleted</span>
        </div>
      </div>
    );
  }

  if (message.type === 'system') {
    const systemText = formatSystemMessageText(message, currentUser?.id);
    return (
      <div className="system_message" data-message-id={message._id}>
        <span className="system_message_text">{systemText}</span>
      </div>
    );
  }

  const renderMessageContent = () => {
    switch (message.type) {
      case 'text':
        return (
          <div className="text_message">
            {message.replyTo && (
              <div className="reply_reference">
                {/* <div className="reply_line"></div> */}
                <div className="reply_content">
                  <div className="reply_sender">{message.replyTo.sender.first_name}</div>
                  <p className="reply_text">{message.replyTo.content.text}</p>
                </div>
              </div>
            )}
            {message.story && (
              <StoryReply story={message.story} isOwn={isOwnMessage} />
            )}
            {isOwnMessage && isEditing ? (
              <div className="message_edit_wrapper">
                <textarea
                  className="message_edit_textarea"
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  rows={2}
                  autoFocus
                />
                <div className="message_edit_actions">
                  <button className="message_edit_btn cancel" onClick={cancelEdit}>Cancel</button>
                  <button className="message_edit_btn save" onClick={submitEdit}>Save</button>
                </div>
              </div>
            ) : (
              <p className={`message_text${isSingleEmoji(message.content.text) ? ' message_text_big_emoji' : ''}`}>
                {message.content.text}
              </p>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="media_message">
            {message.content.media?.map((media, index) => (
              <img
                key={index}
                src={resolveMediaUrl(media.url)}
                alt="Message"
                className="message_image"
                onClick={() => window.open(resolveMediaUrl(media.url), '_blank', 'noopener,noreferrer')}
              />
            ))}
          </div>
        );

      case 'video':
        return (
          <div className="media_message">
            {message.content.media?.map((media, index) => (
              <video
                key={index}
                src={resolveMediaUrl(media.url)}
                controls
                className="message_video"
                preload="metadata"
              />
            ))}
          </div>
        );

      case 'audio':
        const audioMedia = message.content.media?.[0];
        const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
        
        return (
          <div className="audio_message">
            <audio
              ref={audioRef}
              src={resolveMediaUrl(audioMedia?.url)}
              preload="metadata"
              style={{ display: 'none' }}
              onError={handleAudioError}
            />
            
            <button
              className={`audio_play_button ${isPlaying ? 'playing' : ''}`}
              onClick={handleAudioPlay}
              disabled={!audioMedia?.url}
            >
              {isPlaying ? '⏸' : '▶️'}
            </button>
            
            <div className="audio_waveform">
              {[...Array(20)].map((_, index) => {
                const barHeight = 4 + Math.random() * 16; // Random height for visual effect
                const isPlayed = (index / 20) * 100 <= progress;
                
                return (
                  <div
                    key={index}
                    className={`audio_waveform_bar ${isPlayed ? 'played' : ''}`}
                    style={{ height: `${barHeight}px` }}
                  />
                );
              })}
            </div>
            
            <span className="audio_duration">
              {formatDuration(currentTime || 0)}
            </span>
          </div>
        );
      
      case 'file':
        return (
          <div className="file_message">
            {message.content.media?.map((media, index) => {
              const fileKey = `${message._id}-${index}`;
              const isPdf = isPdfFile(media);
              const canOpenPdf = isOwnMessage || !!downloadedFiles[fileKey];
              const pdfUrl = resolveMediaUrl(media.url || media.path);

              return (
              <div
                key={index}
                className="file_attachment"
                onClick={() => {
                  if (isPdf && canOpenPdf) {
                    window.open(pdfUrl, '_self');
                  }
                }}
                style={{ cursor: isPdf && canOpenPdf ? 'pointer' : 'default' }}
              >
                <div className="file_icon">📎</div>
                <div className="file_info">
                  <span className="file_name">{media.name}</span>
                  <span className="file_size">
                    {media.size ? `${(media.size / 1024 / 1024).toFixed(2)} MB` : ''}
                  </span>
                </div>
                {!isOwnMessage && !downloadedFiles[fileKey] && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFileDownload(media, index);
                    }}
                    className="file_download"
                    title="Download file"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    ⬇️
                  </button>
                )}
              </div>
            );})}
          </div>
        );
      
      case 'call':
        const callInfo = message.content.call;
        return (
          <div className="call_icon_wrapper">
            <div className="call_icon">
              {callInfo.type === 'video' ? '📹' : '📞'}
            </div>
          </div>
        );
      
      case 'contact':
        const contact = message.content.contact;
        const contactAvatar = contact?.avatar || contact?.picture || '/images/default_profile.png';
        return (
          <div className="contact_message">
            <div className="contact_card">
              <div className="contact_identity">
                <img
                  src={contactAvatar}
                  alt={contact?.name || 'Contact'}
                  className="contact_avatar"
                />
                <div className="contact_details">
                  <span className="contact_name">{contact?.name || 'Shared contact'}</span>
                  {/* <span className="contact_phone">{contactSubtext}</span> */}
                  {/* <span className="contact_caption">
                    {canCallContact ? 'Tap below to call this contact' : 'Contact shared from messenger'}
                  </span> */}
                </div>
              </div>
              {/* <button 
                className="contact_action_btn"
                onClick={() => {
                  // In a real app, this would add to contacts or call
                  if (contact?.phone) {
                    window.open(`tel:${contact.phone}`, '_self');
                  }
                }}
                disabled={!contact?.phone}
              >
                📞
              </button> */}
            </div>
          </div>
        );
      
      case 'location':
        const location = message.content.location;
        const mapUrl = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
        return (
          <div className="location_message">
            <div className="location_header">
              <div className="location_icon notranslate">📍</div>
              <span className="location_label">Location</span>
            </div>
            <div className="location_card">
              <div className="location_preview">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyA0s1a7phLN0iaD6-UE7m4qP-z21pH0eSc&q=${location.latitude},${location.longitude}&zoom=15`}
                  width="100%"
                  height="150"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                  onError={(e) => {
                    // Fallback if Google Maps fails
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                ></iframe>
                <div className="location_fallback" style={{ display: 'none' }}>
                  <div className="location_coords">
                    📍 {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </div>
                </div>
              </div>
              <button 
                className="location_action_btn"
                onClick={() => window.open(mapUrl, '_blank')}
              >
                Open in Maps
              </button>
            </div>
          </div>
        );
      
      default:
        return <p className="message_text">Unsupported message type</p>;
    }
  };

  // Special handling for call messages - no message_wrapper
  if (message.type === 'call') {
    return (
      <div className={`message_bubble ${isOwnMessage ? 'own' : 'other'}`} data-message-id={message._id}>
        {showAvatar && (
          <img
            src={message.sender.picture}
            alt={message.sender.first_name}
            className="sender_avatar"
          />
        )}
        
        <div className="message_content">
          {renderMessageContent()}
        </div>
        
        {showMeta && (
        <div className="message_meta">
          {message.edited?.isEdited && (
            <span className="edited_label">Edited</span>
          )}
          {isStarred && (
            <span className="star_indicator" title="Starred">★</span>
          )}
          <span className="message_time">{formatTime(message.createdAt)}</span>
          {isOwnMessage && message.read && message.read.length > 0 && (
            <span className="read_status">✓✓</span>
          )}
        </div>
        )}
      </div>
    );
  }

  // Regular message handling with message_wrapper
  return (
    <div className={`message_bubble ${isOwnMessage ? 'own' : 'other'} ${isSelecting && isSelected ? 'selected' : ''}`} data-message-id={message._id}>
      {showAvatar && (
        <img
          src={message.sender.picture}
          alt={message.sender.first_name}
          className="sender_avatar"
        />
      )}
      
      <div className="message_wrapper" ref={menuRef}>
        {isSelecting && (
          <input
            type="checkbox"
            className="message_select_checkbox"
            checked={!!isSelected}
            onChange={() => onToggleSelect && onToggleSelect(message._id)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {showSenderName && (
          <div className="message_sender_name">
            {message.sender.first_name}
          </div>
        )}
        {message.forwarded?.isForwarded && (
          <div className="forwarded_label">Forwarded</div>
        )}
        <div 
          className={`message_content ${isOwnMessage && isEditing ? 'is_editing' : ''}`}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!isSelecting && !disableInteractions) setShowMenu(true);
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={clearLongPressTimer}
          onTouchCancel={clearLongPressTimer}
          onClick={() => {
            clearLongPressTimer();
            if (isSelecting) {
              onToggleSelect && onToggleSelect(message._id);
              return;
            }
            closeMenu();
          }}
        >
          {renderMessageContent()}
          
          {/* Message reactions */}
          {message.reactions && message.reactions.length > 0 && (
            <div className="message_reactions">
              {message.reactions.map((reaction, index) => (
                <span
                  key={index}
                  className="reaction_emoji"
                  onClick={() => handleReaction(reaction.emoji)}
                >
                  {reaction.emoji}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {showMeta && (
        <div className="message_meta">
          {message.edited?.isEdited && (
            <span className="edited_label">Edited</span>
          )}
          {isStarred && (
            <span className="star_indicator" title="Starred">★</span>
          )}
          <span className="message_time">{formatTime(message.createdAt)}</span>
          {isOwnMessage && message.read && message.read.length > 0 && (
            <span className="read_status">✓✓</span>
          )}
        </div>
        )}

        {/* Message menu */}
        {showMenu && (
          <div className="message_menu">
            <div className="quick_reactions">
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  className="reaction_btn"
                  onClick={() => handleReaction(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {/* <button
              className="menu_item"
              onClick={() => setShowMenu(false)}
              aria-label="Back"
              hidden
              title="Back"
            >
              ⬅️
            </button> */}
            {isOwnMessage && (
              <button
                className="menu_item"
                onClick={() => {
                  closeMenu();
                  onShowMessageInfo && onShowMessageInfo(message);
                }}
                title="Message info"
              >
                Message info
              </button>
            )}
            <button
              className="menu_item primary"
              onClick={() => {
                closeMenu();
                onReply(message);
              }}
            >
              Reply
            </button>
            {isOwnMessage && message.type === 'text' && !message.deleted?.isDeleted && isWithinEditWindow && (
              <button
                className="menu_item"
                onClick={startEdit}
              >
                Edit
              </button>
            )}
            {canForward && (
              <button
                className="menu_item"
                onClick={() => {
                  closeMenu();
                  onForward && onForward(message);
                }}
              >
                Forward
              </button>
            )}
            <button
              className={`menu_item star ${isStarred ? 'active' : ''}`}
              onClick={handleStar}
              title={isStarred ? 'Unstar message' : 'Star message'}
            >
              {isStarred ? '★' : '⭐'}
            </button>
            {!isDeleted && (
              <button
                className="menu_item danger"
                onClick={() => {
                  closeMenu();
                  onDelete(message._id, false);
                }}
              >
                Delete for me
              </button>
            )}
            {isOwnMessage && !isDeleted && isWithinDeleteForEveryoneWindow && (
              <button
                className="menu_item danger"
                onClick={() => {
                  closeMenu();
                  onDelete(message._id, true);
                }}
              >
                Delete for everyone
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default function MessagesList({ messages, currentUser, onReact, onDelete, onReply, onStar, onEdit, onForward, conversation, onShowMessageInfo, disableInteractions = false }) {
  const emptySet = new Set();
  const noop = () => {};
  const props = arguments[0] || {};
  const isSelecting = props.isSelecting || false;
  const selectedMessageIds = props.selectedMessageIds || emptySet;
  const onToggleSelect = props.onToggleSelect || noop;
  const starRefreshKey = props.starRefreshKey || 0;
  // Group messages by date and add day separators
  const renderMessagesWithSeparators = () => {
    if (!messages || messages.length === 0) {
      return null;
    }

    const elements = [];
    let currentDate = null;

    let prevMessage = null;
    messages.forEach((message, index) => {
      const messageDate = new Date(message.createdAt);
      
      // Check if we need to add a day separator
      if (currentDate === null || !isSameDay(currentDate, messageDate)) {
        currentDate = messageDate;
        elements.push(
          <div key={`date-${message._id}`} className="day_separator">
            <span className="day_separator_text">{formatDateForSeparator(messageDate)}</span>
          </div>
        );
      }

      // Render system messages (e.g. "X left the group", "Y added Z") inline as a centered notice
      if (message.type === 'system') {
        const systemText = formatSystemMessageText(message, currentUser?.id);
        elements.push(
          <div key={message._id} className="system_message" data-message-id={message._id}>
            <span className="system_message_text">{systemText}</span>
          </div>
        );
        prevMessage = null;
        return;
      }

      // Determine if this message is from the same sender as previous (and on same day)
      const isGroup = conversation?.type === 'group';
      const prevSameSender = prevMessage && prevMessage.sender && message.sender && (prevMessage.sender._id === message.sender._id) && isSameDay(prevMessage.createdAt, message.createdAt);
      // Determine if the next message is from the same sender (so we can hide timestamp/avatar for intermediate messages)
      const nextMessage = messages[index + 1];
      const nextSameSender = nextMessage && nextMessage.sender && message.sender && (nextMessage.sender._id === message.sender._id) && isSameDay(nextMessage.createdAt, message.createdAt);

      // Add the message, passing flags to hide repeated avatar/name on consecutive messages
      elements.push(
        <MessageBubble
          key={message._id}
          message={message}
          currentUser={currentUser}
          onReact={onReact}
          onDelete={onDelete}
          onReply={onReply}
          onStar={onStar}
          onEdit={onEdit}
          onForward={onForward}
          conversation={conversation}
          isSelecting={isSelecting}
          isSelected={selectedMessageIds?.has?.(message._id)}
          onToggleSelect={onToggleSelect}
          starRefreshKey={starRefreshKey}
          onShowMessageInfo={onShowMessageInfo}
          disableInteractions={disableInteractions}
          showSenderName={isGroup && ! (message.sender._id === currentUser.id) && !prevSameSender}
          showAvatar={!(message.sender._id === currentUser.id) && !prevSameSender}
          showMeta={!nextSameSender}
        />
      );

      // update prevMessage to current message (only message entries, not separators)
      prevMessage = message;
    });

    return elements;
  };

  return (
    <div className="messages_list">
      {renderMessagesWithSeparators()}
    </div>
  );
}
